<template>
  <div class="matter-states-viz">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>公式：</strong>T_gas > T_liquid > T_solid$</div>
    </div>

    <div class="main-layout vertical">
      <div class="canvas-box simulation">
        <svg width="100%" height="240" viewBox="0 0 400 240">
          <rect width="400" height="240" fill="rgba(255,255,255,0.02)" />
          
          <!-- Particles -->
          <g v-for="p in particles" :key="p.id">
             <circle :cx="p.x" :cy="p.y" :r="radius" :fill="p.color" opacity="0.8" />
             <circle v-if="state === 'plasma'" :cx="p.x" :cy="p.y" :r="radius + 2" :fill="p.color" opacity="0.2">
                <animate attributeName="opacity" values="0.2;0.5;0.2" dur="0.5s" repeatCount="indefinite" />
             </circle>
          </g>

          <text x="20" y="30" fill="#888" font-size="8">粒子动能: {{ (temp / 10).toFixed(1) }} eV</text>
        </svg>

        <div class="hud-top">
           <div class="stat">
              <label>当前状态: {{ stateName }}</label>
           </div>
        </div>
      </div>

      <div class="bottom-controls">
        <div class="control-card glass">
          <div class="row items-center">
             <label>选择物态</label>
             <el-radio-group v-model="state" size="mini">
                <el-radio-button label="solid">固态</el-radio-button>
                <el-radio-button label="liquid">液态</el-radio-button>
                <el-radio-button label="gas">气态</el-radio-button>
                <el-radio-button label="plasma">等离子态</el-radio-button>
             </el-radio-group>
          </div>
          <div class="row items-center mt-2">
             <label>环境温度</label>
             <el-slider v-model="temp" :min="1" :max="500" size="mini"></el-slider>
          </div>
        </div>

        <div class="discovery-note" v-latex>
           <p><strong>物理详解：</strong>物质的状态是由**热运动（动能）**与**分子间作用力（势能）**的博弈决定的。**固态**中引力主导，粒子在格点振动；**液态**中动能增加，粒子可相对滑动但仍受吸引；**气态**中动能克服引力，粒子自由飞翔。当温度极高时，原子被电离成电子和离子，形成第四态——**等离子态**。这一转变体现了宏观物性对微观力学平衡的极端敏感性。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MatterStatesViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      state: 'solid',
      temp: 10,
      particles: [],
      timer: null,
      radius: 5
    }
  },
  computed: {
    stateName() {
       const map = { solid: '固态 (有序振动)', liquid: '液态 (近程有序)', gas: '气态 (杂乱无章)', plasma: '等离子态 (高度电离)' };
       return map[this.state];
    }
  },
  watch: {
    state() { this.initParticles(); }
  },
  methods: {
    initParticles() {
       this.particles = [];
       const count = 40;
       for (let i = 0; i < count; i++) {
          let x, y, color;
          if (this.state === 'solid') {
             x = 100 + (i % 8) * 20;
             y = 120 + Math.floor(i / 8) * 20;
             color = '#1e90ff';
          } else {
             x = Math.random() * 300 + 50;
             y = Math.random() * 140 + 50;
             color = this.state === 'plasma' ? (Math.random() > 0.5 ? '#ff4757' : '#ffd700') : '#00d2ff';
          }
          this.particles.push({
             id: i,
             x, y, 
             ox: x, oy: y,
             vx: (Math.random() - 0.5) * 2,
             vy: (Math.random() - 0.5) * 2,
             color
          });
       }
    },
    update() {
       const speed = this.temp / 50;
       this.particles.forEach(p => {
          if (this.state === 'solid') {
             p.x = p.ox + Math.sin(Date.now()*0.01 + p.id) * speed;
             p.y = p.oy + Math.cos(Date.now()*0.01 + p.id) * speed;
          } else if (this.state === 'liquid') {
             p.x += p.vx * speed;
             p.y += p.vy * speed;
             if (p.x < 50 || p.x > 350) p.vx *= -1;
             if (p.y > 220) p.vy *= -1;
             p.vy += 0.1; // Gravity for liquid
             if (p.y < 120) p.y = 120;
          } else {
             p.x += p.vx * speed * 2;
             p.y += p.vy * speed * 2;
             if (p.x < 0 || p.x > 400) p.vx *= -1;
             if (p.y < 0 || p.y > 240) p.vy *= -1;
          }
       });
       this.timer = requestAnimationFrame(this.update);
    }
  },
  mounted() { this.initParticles(); this.update(); },
  beforeDestroy() { cancelAnimationFrame(this.timer); }
}
</script>

<style scoped lang="scss">
.matter-states-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.canvas-box { background: #020205; border-radius: 12px; position: relative; border: 1px solid rgba(255,255,255,0.05); }
.bottom-controls { flex: 0.6; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: rgba(255,255,255,0.02); border-radius: 12px; padding: 15px; }
.discovery-note { background: rgba(0, 210, 255, 0.05); padding: 12px 18px; border-radius: 12px; p { margin: 0; font-size: 0.75rem; color: #aaa; line-height: 1.5; strong { color: #fff; } } }
</style>
